import type { ButtonProps } from "@mui/material/Button";
import type { Theme, CSSObject, Components, ComponentsVariants } from "@mui/material/styles";

import { varAlpha } from "minimal-shared/utils";

import { buttonClasses } from "@mui/material/Button";

// ----------------------------------------------------------------------

/**
 * TypeScript (type definition and extension)
 * @to {@link file://./../../extend-theme-types.d.ts}
 */

export type ButtonExtendVariant = {
  soft: true;
};

// ----------------------------------------------------------------------

const COLORS = ["primary", "secondary", "info", "success", "warning", "error", "white"] as const;

type PaletteColor = (typeof COLORS)[number];

/** Laranja mais escuro para o estado active do botão primary (palette.primary.dark é quase preto) */
const PRIMARY_ACTIVE_BG = "#c44a10";

const DISABLED_BG = "var(--palette-action-disabledBackground)";
const DISABLED_TEXT = "var(--palette-text-disabled)";

// ----------------------------------------------------------------------

function styleColors(ownerState: ButtonProps, styles: (val: PaletteColor) => CSSObject) {
  return COLORS.reduce<CSSObject>((acc, color) => {
    if (!ownerState.disabled && ownerState.color === color) {
      acc = { ...acc, ...styles(color) };
    }
    return acc;
  }, {});
}

// ----------------------------------------------------------------------

const MuiButtonBase: Components<Theme>["MuiButtonBase"] = {
  /** **************************************
   * STYLE
   *************************************** */
  styleOverrides: { root: ({ theme }) => ({ fontFamily: theme.typography.fontFamily }) },
};

// ----------------------------------------------------------------------

const softVariant: Record<string, ComponentsVariants<Theme>["MuiButton"]> = {
  colors: COLORS.map((color) => ({
    props: ({ ownerState }) =>
      !ownerState.disabled && ownerState.variant === "soft" && ownerState.color === color,
    style: ({ theme }) => ({
      color: theme.vars.palette[color].dark,
      backgroundColor: varAlpha(theme.vars.palette[color].mainChannel, 0.16),
      "&:hover": { backgroundColor: varAlpha(theme.vars.palette[color].mainChannel, 0.32) },
      ...theme.applyStyles("dark", {
        color: theme.vars.palette[color].light,
      }),
    }),
  })),
  base: [
    {
      props: ({ ownerState }) => ownerState.variant === "soft",
      style: ({ theme }) => ({
        backgroundColor: varAlpha(theme.vars.palette.grey["500Channel"], 0.08),
        "&:hover": { backgroundColor: varAlpha(theme.vars.palette.grey["500Channel"], 0.24) },
        [`&.${buttonClasses.disabled}`]: {
          backgroundColor: DISABLED_BG,
          color: DISABLED_TEXT,
        },
        // v7: LoadingButton fundiu no Button; posição do indicador vem das
        // classes loadingPosition{Start,End} na raiz + loadingIndicator.
        [`&.${buttonClasses.loadingPositionStart} .${buttonClasses.loadingIndicator}`]: {
          left: 14,
        },
        [`&.${buttonClasses.loadingPositionEnd} .${buttonClasses.loadingIndicator}`]: { right: 14 },
        [`&.${buttonClasses.sizeSmall}`]: {
          [`&.${buttonClasses.loadingPositionStart} .${buttonClasses.loadingIndicator}`]: {
            left: 10,
          },
          [`&.${buttonClasses.loadingPositionEnd} .${buttonClasses.loadingIndicator}`]: {
            right: 10,
          },
        },
      }),
    },
  ],
};

const MuiButton: Components<Theme>["MuiButton"] = {
  /** **************************************
   * DEFAULT PROPS
   *************************************** */
  defaultProps: { color: "inherit", disableElevation: true },

  /** **************************************
   * STYLE
   *************************************** */
  styleOverrides: {
    root: ({ theme }) => ({
      borderRadius: 8,
      textTransform: "uppercase",
      fontStyle: "normal",
      fontWeight: 700,
      transition: theme.transitions.create(
        ["background-color", "box-shadow", "border-color", "outline", "outline-offset"],
        { duration: theme.transitions.duration.shorter }
      ),
      [`&.${buttonClasses.disabled}`]: {
        cursor: "not-allowed",
      },
      variants: [softVariant.base, softVariant.colors].flat(),
    }),
    /**
     * @variant contained
     */
    contained: ({ theme, ownerState }) => {
      const styled = {
        base: {
          boxShadow: "none",
          "&:hover": { boxShadow: "none" },
          [`&.${buttonClasses.disabled}`]: {
            backgroundColor: DISABLED_BG,
            color: DISABLED_TEXT,
            "&:hover": {
              backgroundColor: DISABLED_BG,
              color: DISABLED_TEXT,
            },
          },
        },
        colors: styleColors(ownerState, (color) => ({
          color: theme.vars.palette[color].contrastText,
          backgroundColor: theme.vars.palette[color].main,
          "&:hover": {
            backgroundColor: theme.vars.palette[color].light,
            boxShadow: theme.vars.customShadows[color],
          },
          "&:active": {
            backgroundColor:
              color === "primary" ? PRIMARY_ACTIVE_BG : theme.vars.palette[color].dark,
            boxShadow: "none",
          },
          "&:focus-visible": {
            outline: "2px solid #fff",
            outlineOffset: "2px",
            boxShadow: `0 0 0 4px ${theme.vars.palette[color].main}`,
          },
          ...theme.applyStyles("dark", {
            backgroundColor: theme.vars.palette[color].main,
            "&:hover": {
              backgroundColor: theme.vars.palette[color].light,
            },
            "&:active": {
              backgroundColor:
                color === "primary" ? PRIMARY_ACTIVE_BG : theme.vars.palette[color].dark,
              boxShadow: "none",
            },
          }),
        })),
        inheritColor: {
          ...(ownerState.color === "inherit" &&
            !ownerState.disabled && {
              color: theme.vars.palette.common.white,
              backgroundColor: theme.vars.palette.grey[800],
              "&:hover": {
                boxShadow: theme.vars.customShadows.z8,
                backgroundColor: theme.vars.palette.grey[700],
              },
              "&:active": {
                backgroundColor: theme.vars.palette.grey[600],
                boxShadow: "none",
              },
              "&:focus-visible": {
                outline: "2px solid #fff",
                outlineOffset: "2px",
                boxShadow: `0 0 0 4px ${theme.vars.palette.grey[700]}`,
              },
              ...theme.applyStyles("dark", {
                color: theme.vars.palette.grey[800],
                backgroundColor: theme.vars.palette.common.white,
                "&:hover": {
                  backgroundColor: theme.vars.palette.grey[400],
                  boxShadow: theme.vars.customShadows.z8,
                },
                "&:active": {
                  backgroundColor: theme.vars.palette.grey[300],
                  boxShadow: "none",
                },
                "&:focus-visible": {
                  outline: "2px solid #fff",
                  outlineOffset: "2px",
                  boxShadow: `0 0 0 4px ${theme.vars.palette.grey[600]}`,
                },
              }),
            }),
        },
      };
      return { ...styled.base, ...styled.inheritColor, ...styled.colors };
    },
    /**
     * @variant outlined
     */
    outlined: ({ theme, ownerState }) => {
      const styled = {
        colors: styleColors(ownerState, (color) => ({
          borderColor: varAlpha(theme.vars.palette[color].mainChannel, 0.48),
          borderWidth: "1.5px",
          borderStyle: "solid",
          "&:focus-visible": {
            outline: "2px solid #fff",
            outlineOffset: "2px",
            boxShadow: `0 0 0 4px ${theme.vars.palette[color].main}`,
          },
        })),
        inheritColor: {
          ...(ownerState.color === "inherit" &&
            !ownerState.disabled && {
              borderColor: varAlpha(theme.vars.palette.grey["500Channel"], 0.32),
              borderWidth: "1.5px",
              borderStyle: "solid",
              "&:hover": { backgroundColor: theme.vars.palette.action.hover },
              "&:focus-visible": {
                outline: "2px solid #fff",
                outlineOffset: "2px",
                boxShadow: `0 0 0 4px ${theme.vars.palette.grey[600]}`,
              },
            }),
        },
        base: {
          borderWidth: "1.5px",
          borderStyle: "solid",
          "&:hover": { borderColor: "currentColor", boxShadow: "0 0 0 0.75px currentColor" },
          [`&.${buttonClasses.disabled}`]: {
            borderColor: DISABLED_BG,
            color: DISABLED_TEXT,
            backgroundColor: "transparent",
          },
        },
      };
      return { ...styled.base, ...styled.inheritColor, ...styled.colors };
    },
    /**
     * @variant text
     */
    text: ({ ownerState, theme }) => {
      const styled = {
        base: {
          "&:focus-visible": {
            outline: "2px solid #fff",
            outlineOffset: "2px",
            boxShadow: `0 0 0 4px ${theme.vars.palette.primary.main}`,
          },
          [`&.${buttonClasses.disabled}`]: {
            color: DISABLED_TEXT,
            backgroundColor: "transparent",
          },
        },
        inheritColor: {
          ...(ownerState.color === "inherit" &&
            !ownerState.disabled && {
              "&:hover": { backgroundColor: theme.vars.palette.action.hover },
              "&:focus-visible": {
                outline: "2px solid #fff",
                outlineOffset: "2px",
                boxShadow: `0 0 0 4px ${theme.vars.palette.grey[600]}`,
              },
            }),
        },
      };
      return { ...styled.base, ...styled.inheritColor };
    },
    /**
     * @sizes
     */
    sizeSmall: ({ ownerState }) => ({
      height: 30,
      ...(ownerState.variant === "text"
        ? { paddingLeft: "4px", paddingRight: "4px" }
        : { paddingLeft: "8px", paddingRight: "8px" }),
    }),
    sizeMedium: ({ ownerState }) => ({
      ...(ownerState.variant === "text"
        ? { paddingLeft: "8px", paddingRight: "8px" }
        : { paddingLeft: "12px", paddingRight: "12px" }),
    }),
    sizeLarge: ({ ownerState }) => ({
      height: 48,
      ...(ownerState.variant === "text"
        ? { paddingLeft: "10px", paddingRight: "10px" }
        : { paddingLeft: "16px", paddingRight: "16px" }),
    }),
  },
};

// ----------------------------------------------------------------------

export const button = { MuiButtonBase, MuiButton };
